import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PANT_TUXEDO_FRONT_POCKET_OPENING } from "@/data/styleDefaults";

/**
 * Pant Tuxedo toggle — when enabled, sets four pant-styling fields to their
 * tuxedo defaults (satin waistband, satin besom on hip pockets, satin side
 * seam piping, satin on front-pocket besom/welt). Toggling off clears those
 * four fields.
 *
 * This is purely a state-shortcut: it doesn't lock the values, the user can
 * still override any field afterwards. Useful for the common "I want tuxedo
 * pants" case so the tailor doesn't have to remember the satin codes.
 */
const TUX_PRESET = {
  pant_back_pocket_besom_fabric: "Hip pocket besom with satin",                  // T-325A
  pant_waistband_fabric:         "Satin waistband",                              // T-33A1
  pant_satin_position:           "Side seam with satin (1.0cm width)",           // T-331P
  pant_front_pocket_opening:     PANT_TUXEDO_FRONT_POCKET_OPENING,               // T-314P
};

const TUX_FIELDS = Object.keys(TUX_PRESET);

export function isPantTuxedoActive(pantStyling) {
  return TUX_FIELDS.every((f) => pantStyling?.[f] === TUX_PRESET[f]);
}

export default function PantTuxedoToggle({
  pantStyling,
  setPantStyling,
  testId = "pant-tuxedo-toggle",
}) {
  const active = isPantTuxedoActive(pantStyling);

  const toggle = () => {
    if (active) {
      // Clear only the fields the toggle controls
      const next = { ...pantStyling };
      for (const f of TUX_FIELDS) delete next[f];
      setPantStyling(next);
    } else {
      setPantStyling({ ...pantStyling, ...TUX_PRESET });
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      onClick={toggle}
      className={`h-7 text-[11px] gap-1 transition-colors ${
        active
          ? "bg-amber-500 hover:bg-amber-600 text-black"
          : "bg-transparent border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
      }`}
      data-testid={testId}
      title={
        active
          ? "Clear satin pant codes (T-325A, T-33A1, T-331P, T-314P)"
          : "Activate tuxedo pant codes (T-325A, T-33A1, T-331P, T-314P)"
      }
    >
      <Sparkles size={12} /> {active ? "Tuxedo ON" : "Pant Tuxedo"}
    </Button>
  );
}
